import { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import { CheckCircle, XCircle, Loader2, Mail } from 'lucide-react';
import { motion } from 'framer-motion';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

axios.defaults.withCredentials = true;

export default function VerifyEmailPage() {
  const [status, setStatus] = useState('loading'); // loading, success, error, already
  const [message, setMessage] = useState('');
  const calledRef = useRef(false);

  useEffect(() => {
    if (calledRef.current) return;
    calledRef.current = true;

    const params = new URLSearchParams(window.location.search);
    const token = params.get('token');

    if (!token) {
      setStatus('error');
      setMessage('Token verifikasi tidak ditemukan di URL.');
      return;
    }

    const verifyEmail = async () => {
      try {
        const res = await axios.post(`${API}/auth/verify-email`, { token });
        if (res.data.message.includes('sudah terverifikasi')) {
          setStatus('already');
        } else {
          setStatus('success');
        }
        setMessage(res.data.message);
      } catch (err) {
        setStatus('error');
        setMessage(
          err.response?.data?.detail || 'Token verifikasi tidak valid atau sudah kadaluarsa.'
        );
      }
    };

    verifyEmail();
  }, []);

  const goToLogin = () => {
    window.location.href = '/';
  };

  return (
    <div className="min-h-screen bg-slate-900 flex items-center justify-center p-4">
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5 }}
        className="bg-slate-800/80 backdrop-blur-lg border border-slate-700/50 rounded-2xl shadow-2xl p-8 max-w-md w-full text-center"
      >
        {/* Icon */}
        <div className="mb-6">
          {status === 'loading' && (
            <div className="w-16 h-16 mx-auto bg-teal-500/20 rounded-full flex items-center justify-center">
              <Loader2 className="w-8 h-8 text-teal-400 animate-spin" />
            </div>
          )}
          {status === 'success' && (
            <motion.div
              initial={{ scale: 0 }}
              animate={{ scale: 1 }}
              transition={{ type: 'spring', stiffness: 200, damping: 10 }}
              className="w-16 h-16 mx-auto bg-green-500/20 rounded-full flex items-center justify-center"
            >
              <CheckCircle className="w-8 h-8 text-green-400" />
            </motion.div>
          )}
          {status === 'already' && (
            <motion.div
              initial={{ scale: 0 }}
              animate={{ scale: 1 }}
              transition={{ type: 'spring', stiffness: 200, damping: 10 }}
              className="w-16 h-16 mx-auto bg-blue-500/20 rounded-full flex items-center justify-center"
            >
              <Mail className="w-8 h-8 text-blue-400" />
            </motion.div>
          )}
          {status === 'error' && (
            <motion.div
              initial={{ scale: 0 }}
              animate={{ scale: 1 }}
              transition={{ type: 'spring', stiffness: 200, damping: 10 }}
              className="w-16 h-16 mx-auto bg-red-500/20 rounded-full flex items-center justify-center"
            >
              <XCircle className="w-8 h-8 text-red-400" />
            </motion.div>
          )}
        </div>

        {/* Title */}
        <h1 className="text-2xl font-bold text-white mb-2" data-testid="verify-email-title">
          {status === 'loading' && 'Memverifikasi Email...'}
          {status === 'success' && 'Email Terverifikasi!'}
          {status === 'already' && 'Sudah Terverifikasi'}
          {status === 'error' && 'Verifikasi Gagal'}
        </h1>

        {/* Message */}
        <p data-testid="verify-email-message" className={`mb-6 ${
          status === 'success' ? 'text-green-300' :
          status === 'already' ? 'text-blue-300' :
          status === 'error' ? 'text-red-300' :
          'text-slate-400'
        }`}>
          {status === 'loading' ? 'Mohon tunggu sebentar...' : message}
        </p>

        {/* Button */}
        {status !== 'loading' && (
          <motion.button
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.3 }}
            onClick={goToLogin}
            data-testid="verify-go-to-login-btn"
            className="w-full py-3 px-6 bg-gradient-to-r from-teal-600 to-cyan-700 hover:from-teal-500 hover:to-cyan-600 text-white font-semibold rounded-xl transition-all duration-200 shadow-lg hover:shadow-teal-500/30"
          >
            Ke Halaman Login
          </motion.button>
        )}
      </motion.div>
    </div>
  );
}
